import { clsx } from 'clsx'
import { useLatestCallback } from 'hook/useLatestCallback'
import { PointerEvent, useEffect, useMemo, useRef, useState } from 'react'
import css from './Ruler.module.scss'

type Props = {
  min: number
  max: number
  value: number
  /** шаг деления, по умолчанию 1 */
  step?: number
  /** расстояние между соседними делениями в px */
  gap?: number
  /** каждое N-е деление — крупное */
  majorEvery?: number
  /** каждое N-е деление подписывается значением */
  labelEvery?: number
  className?: string
  onChange(value: number): void
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

export function Ruler(props: Props) {
  const {
    min,
    max,
    value,
    step = 1,
    gap = 18,
    majorEvery = 5,
    labelEvery = 10,
    className,
  } = props

  const onChange = useLatestCallback(props.onChange)

  const viewport = useRef<HTMLDivElement>(null)
  const drag = useRef<{ startX: number, startValue: number } | null>(null)

  // Плавающее значение во время перетаскивания (null — не тянем)
  const [floating, setFloating] = useState<number | null>(null)
  const current = floating ?? value

  const ticks = useMemo(() => {
    const count = Math.round((max - min) / step)
    return Array.from({ length: count + 1 }, (_, i) => min + i * step)
  }, [min, max, step])

  const offsetPx = ((current - min) / step) * gap

  const emit = (next: number) => {
    const clamped = clamp(next, min, max)
    const snapped = Math.round((clamped - min) / step) * step + min
    if (snapped !== value) onChange(snapped)
  }

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button > 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = { startX: event.clientX, startValue: current }
    setFloating(current)
  }

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const state = drag.current
    if (!state) return

    // сдвиг влево увеличивает значение
    const delta = ((state.startX - event.clientX) / gap) * step
    const next = clamp(state.startValue + delta, min, max)
    setFloating(next)
    emit(next)
  }

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    drag.current = null
    setFloating(null)
    try {
      event.currentTarget.releasePointerCapture(event.pointerId)
    } catch {
      // уже снят или отменён
    }
  }

  // Колесо мыши — нативный слушатель, чтобы можно было preventDefault
  useEffect(() => {
    const el = viewport.current
    if (!el) return

    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      const dir = (event.deltaY || event.deltaX) > 0 ? 1 : -1
      emit(value + dir * step)
    }

    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [value, step, min, max])

  return <div className={clsx(css.ruler, className)}>
    <div className={css.arrow} />

    <div
      ref={viewport}
      className={css.viewport}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div
        className={clsx(css.track, floating == null && css.smooth)}
        style={{ transform: `translateX(${-offsetPx}px)` }}
      >
        {ticks.map(tick => {
          const major = tick % majorEvery === 0
          const label = tick % labelEvery === 0

          return <div
            key={tick}
            className={css.tick}
            style={{ left: `${((tick - min) / step) * gap}px` }}
          >
            <span className={clsx(css.line, major ? css.major : css.minor)} />
            {label && <span className={css.label}>{tick}</span>}
          </div>
        })}
      </div>
    </div>
  </div>
}
